import React from 'react';
import { Icon } from './icons.jsx';
// GlobalSearch — Cmd+K / Ctrl+K modal de busca global
const GlobalSearch = ({ stores, internalUsers, onNav, onClose, storesLoading, useRealStores }) => {
  const [q, setQ] = React.useState('');
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    inputRef.current?.focus();
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Busca em lojas
  const storeResults = React.useMemo(() => {
    if (!q.trim() || q.length < 2) return [];
    const ql = q.toLowerCase();
    return (stores || []).filter(s =>
      s.name.toLowerCase().includes(ql) ||
      s.domain.toLowerCase().includes(ql) ||
      (s.customDomain && s.customDomain.toLowerCase().includes(ql)) ||
      (s.ownerName && s.ownerName.toLowerCase().includes(ql)) ||
      (s.ownerEmail && s.ownerEmail.toLowerCase().includes(ql)) ||
      (s.cnpj && s.cnpj.includes(q)) ||
      s.id.toLowerCase().includes(ql)
    ).slice(0, 6);
  }, [q, stores]);

  // Busca em usuários internos
  const userResults = React.useMemo(() => {
    if (!q.trim() || q.length < 2) return [];
    const ql = q.toLowerCase();
    return (internalUsers || []).filter(u =>
      u.name.toLowerCase().includes(ql) ||
      u.email.toLowerCase().includes(ql)
    ).slice(0, 3);
  }, [q, internalUsers]);

  // Ações rápidas sempre visíveis
  const quickActions = [
    { label: 'Nova loja',          icon: 'store',    action: () => { onNav({ page: 'nova-loja' });      onClose(); } },
    { label: 'Lojas inadimplentes',icon: 'overdue',  action: () => { onNav({ page: 'inadimplencia' }); onClose(); } },
    { label: 'Auditoria',          icon: 'audit',    action: () => { onNav({ page: 'auditoria' });      onClose(); } },
    { label: 'Usuários internos',  icon: 'users',    action: () => { onNav({ page: 'equipe' });       onClose(); } },
  ];

  const hasResults = storeResults.length + userResults.length > 0;
  const showEmpty  = q.length >= 2 && !hasResults;
  const showRealEmpty = useRealStores && showEmpty && !storesLoading;

  const STATUS_COLOR = {
    ativo:        'var(--success)',
    trial:        'var(--info)',
    inadimplente: 'var(--danger)',
    suspenso:     'var(--text-muted)',
  };
  const STATUS_LABEL = {
    ativo: 'Ativo', trial: 'Trial', inadimplente: 'Inadimplente', suspenso: 'Suspenso',
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(10,22,40,0.5)',
        backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        paddingTop: 100,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: 580, background: 'var(--bg-elevated)',
          borderRadius: 16, boxShadow: 'var(--shadow-lg)',
          border: '1px solid var(--border)',
          overflow: 'hidden',
          animation: 'scale-in 180ms cubic-bezier(0.2,0.8,0.2,1)',
          transformOrigin: 'top center',
        }}
      >
        {/* Input */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '14px 18px', borderBottom: '1px solid var(--border)',
        }}>
          <Icon name="search" size={18} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
          <input
            ref={inputRef}
            type="text"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder={useRealStores ? 'Buscar lojas reais (nome, domínio, e-mail…)…' : 'Buscar loja, domínio, proprietário, CNPJ…'}
            style={{
              flex: 1, border: 'none', background: 'transparent',
              fontSize: 15, color: 'var(--text)', outline: 'none',
              fontFamily: 'inherit',
            }}
          />
          {q && (
            <button onClick={() => setQ('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}>
              <Icon name="x" size={14} />
            </button>
          )}
          <kbd style={{
            fontSize: 11, color: 'var(--text-muted)',
            background: 'var(--bg-subtle)', border: '1px solid var(--border)',
            borderRadius: 5, padding: '2px 7px', flexShrink: 0,
          }}>Esc</kbd>
        </div>

        <div style={{ maxHeight: 460, overflowY: 'auto' }}>
          {/* Resultados de lojas */}
          {storeResults.length > 0 && (
            <>
              <div style={{ padding: '10px 18px 4px', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Lojas</div>
              {storeResults.map(s => (
                <div
                  key={s.id}
                  onClick={() => { onNav({ page: 'loja', store: s }); onClose(); }}
                  style={{
                    padding: '10px 18px', display: 'flex', alignItems: 'center', gap: 12,
                    cursor: 'pointer', transition: 'background 80ms',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-subtle)'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}
                >
                  <div style={{
                    width: 36, height: 36, borderRadius: 8, flexShrink: 0,
                    background: s.color, color: 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12, fontWeight: 700,
                  }}>
                    {s.name.split(' ').filter(w => w.length > 1).slice(0, 2).map(w => w[0]).join('').toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{s.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'JetBrains Mono, monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.domain}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    <span style={{
                      fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
                      color: STATUS_COLOR[s.status],
                      background: `color-mix(in srgb, ${STATUS_COLOR[s.status]} 12%, transparent)`,
                    }}>{STATUS_LABEL[s.status]}</span>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{s.plan?.name || '—'}</span>
                    <Icon name="chevronRight" size={14} style={{ color: 'var(--text-disabled)' }} />
                  </div>
                </div>
              ))}
            </>
          )}

          {/* Resultados de usuários internos */}
          {userResults.length > 0 && (
            <>
              <div style={{ padding: '10px 18px 4px', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Usuários internos</div>
              {userResults.map((u, i) => (
                <div
                  key={i}
                  onClick={() => { onNav({ page: 'equipe' }); onClose(); }}
                  style={{ padding: '10px 18px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', transition: 'background 80ms' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-subtle)'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}
                >
                  <div style={{
                    width: 36, height: 36, borderRadius: 8, flexShrink: 0,
                    background: 'var(--brand-navy)', color: 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12, fontWeight: 700,
                  }}>
                    {u.name.split(' ').map(n => n[0]).slice(0, 2).join('')}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{u.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'JetBrains Mono, monospace' }}>{u.email}</div>
                  </div>
                  <div style={{ flexShrink: 0, fontSize: 12, color: 'var(--text-muted)' }}>{u.role}</div>
                </div>
              ))}
            </>
          )}

          {/* Empty state */}
          {storesLoading && useRealStores && q.length >= 2 && (
            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Carregando lojas reais…</div>
          )}
          {showEmpty && !storesLoading && (
            <div style={{ padding: '36px 24px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              <Icon name="search" size={24} style={{ display: 'block', margin: '0 auto 8px', opacity: 0.3 }} />
              {showRealEmpty ? <>Nenhum resultado real encontrado para <strong>"{q}"</strong></> : <>Nenhum resultado para <strong>"{q}"</strong></>}
            </div>
          )}

          {/* Ações rápidas (quando busca vazia) */}
          {q.length < 2 && (
            <>
              <div style={{ padding: '10px 18px 4px', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Ações rápidas</div>
              {quickActions.map((a, i) => (
                <div
                  key={i}
                  onClick={a.action}
                  style={{ padding: '10px 18px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', transition: 'background 80ms' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-subtle)'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}
                >
                  <div style={{
                    width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                    background: 'var(--bg-subtle)', color: 'var(--text-secondary)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Icon name={a.icon} size={15} />
                  </div>
                  <span style={{ fontSize: 14, color: 'var(--text-secondary)' }}>{a.label}</span>
                  <Icon name="arrowRight" size={13} style={{ color: 'var(--text-disabled)', marginLeft: 'auto' }} />
                </div>
              ))}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '8px 18px', borderTop: '1px solid var(--border)',
          display: 'flex', gap: 14, fontSize: 11, color: 'var(--text-muted)',
          background: 'var(--bg-subtle)',
        }}>
          <span><kbd style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 4, padding: '1px 5px' }}>↑↓</kbd> navegar</span>
          <span><kbd style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 4, padding: '1px 5px' }}>↵</kbd> abrir</span>
          <span><kbd style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 4, padding: '1px 5px' }}>Esc</kbd> fechar</span>
          <span style={{ marginLeft: 'auto' }}>{storeResults.length + userResults.length > 0 ? `${storeResults.length + userResults.length} resultado${storeResults.length + userResults.length !== 1 ? 's' : ''}` : ''}</span>
        </div>
      </div>
    </div>
  );
};

export {
  GlobalSearch,
};
